import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { selectFeel, toggleFlavor } from '../../../vibe/selection';
import FeelWheel from './FeelWheel';
import FlavorChips from './FlavorChips';
import StyleGrid from './StyleGrid';

function OptionalPreferences({ value, feelId, styleIds, flavorIds, disabled, onChange }) {
  return (
    <>
      <StyleGrid
        styleIds={styleIds}
        disabled={disabled}
        onChange={(nextStyleIds) => onChange?.({ ...value, feelId, styleIds: nextStyleIds, flavorIds })}
      />
      <FlavorChips
        flavorIds={flavorIds}
        disabled={disabled}
        onToggle={(flavorId) => onChange?.(toggleFlavor(value, flavorId))}
      />
    </>
  );
}

/**
 * One scrolling Feel → Style → Flavor selector for hosts and guests.
 * Save is the only commit. Draft changes stay local.
 */
export default function VibeSelector({
  value,
  onChange,
  onSave,
  onCancel,
  saveLabel = 'Save My Vibe',
  saving = false,
  savingLabel = 'Saving…',
  showCancel = false,
  compactOptionalPreferences = false,
}) {
  const feelId = value?.feelId || null;
  const styleIds = value?.styleIds || [];
  const flavorIds = value?.flavorIds || [];
  const canSave = Boolean(feelId) && !saving;
  const [moreOpen, setMoreOpen] = useState(false);
  const optionalCount = styleIds.length + flavorIds.length;
  const showOptional = compactOptionalPreferences && moreOpen;

  return (
    <div className={compactOptionalPreferences ? 'space-y-4 pb-4' : 'space-y-6 pb-4'}>
      <FeelWheel
        feelId={feelId}
        disabled={saving}
        onSelect={(nextFeelId) => onChange?.(selectFeel(value, nextFeelId))}
      />
      {compactOptionalPreferences ? (
        <div>
          <button
            type="button"
            aria-expanded={moreOpen}
            aria-controls="vibe-optional-preferences"
            onClick={() => setMoreOpen((open) => !open)}
            className="flex min-h-[44px] w-full items-center justify-between gap-3 py-1 text-left text-sm text-white/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            <span className="min-w-0 truncate">
              More preferences
              {optionalCount > 0 ? (
                <span className="text-white/50">{` · ${optionalCount} selected`}</span>
              ) : null}
            </span>
            <ChevronDownIcon
              aria-hidden="true"
              className={`h-4 w-4 shrink-0 text-white/50 transition-transform motion-reduce:transition-none ${moreOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {showOptional ? (
            <div id="vibe-optional-preferences" data-testid="vibe-optional-preferences" className="mt-3 space-y-6">
              <OptionalPreferences
                value={value}
                feelId={feelId}
                styleIds={styleIds}
                flavorIds={flavorIds}
                disabled={saving}
                onChange={onChange}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <OptionalPreferences
          value={value}
          feelId={feelId}
          styleIds={styleIds}
          flavorIds={flavorIds}
          disabled={saving}
          onChange={onChange}
        />
      )}
      <div className="pt-1">
        <div className="flex gap-2">
          {showCancel ? (
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="w-1/3 rounded-lg border border-white/20 py-3 text-sm font-medium text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-60"
            >
              Cancel
            </button>
          ) : null}
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            className="w-full rounded-lg bg-blue-600 py-3 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50"
          >
            {saving ? savingLabel : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
